import { ShieldCheck, Monitor, QrCode } from 'lucide-react';
import { payeeDisplayName } from '../../utils/upi';

const AVATAR_COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#ef4444', '#14b8a6'];

const initialsOf = (text) => {
  const words = (text || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
};

export function PayeeAvatar({ vpa, name, size = 40 }) {
  const key = (vpa || '').toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  const color = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: `${color}1f`, color, fontSize: size * 0.36 }}
    >
      {initialsOf(name || vpa)}
    </div>
  );
}

/** Scanned payee shown at the top of the pay form. */
export function UpiPayeeCard({ payee, onChange }) {
  const name = payeeDisplayName(payee);
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-surface-200 bg-white">
      <PayeeAvatar vpa={payee.vpa} name={payee.name} size={44} />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-surface-400">Paying</p>
        <p className="text-sm font-semibold text-surface-900 truncate">{name}</p>
        {name !== payee.vpa && <p className="text-xs text-surface-500 truncate">{payee.vpa}</p>}
      </div>
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-surface-100 text-surface-700 hover:bg-surface-200"
        >
          Change
        </button>
      )}
    </div>
  );
}

export function RecentPayeeChips({ payees, onSelect }) {
  if (!payees?.length) return null;
  return (
    <div>
      <p className="text-[11px] font-medium text-surface-400 mb-1.5">Recent</p>
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {payees.map((p) => (
          <button
            key={p.vpa}
            type="button"
            onClick={() => onSelect(p)}
            className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-surface-200 bg-white hover:bg-surface-50 flex-shrink-0 max-w-[180px]"
            title={p.vpa}
          >
            <PayeeAvatar vpa={p.vpa} name={p.name} size={26} />
            <span className="text-xs font-medium text-surface-700 truncate">{p.name || p.vpa}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Small trust badge: the app never touches the bank account or the UPI PIN. */
export function PayTrustNote({ className = '' }) {
  return (
    <div className={`flex items-center justify-center gap-1.5 text-[11px] text-surface-500 ${className}`}>
      <ShieldCheck className="w-3.5 h-3.5 text-income-600 flex-shrink-0" />
      <span>
        <span className="font-semibold text-surface-700">No bank linking</span>
        <span className="mx-1 text-surface-300">·</span>
        Your UPI PIN stays in your UPI app
      </span>
    </div>
  );
}

/** How paying works in a browser, which differs between phone and computer. */
export function WebPayNote({ onPhone, className = '' }) {
  const Icon = onPhone ? QrCode : Monitor;
  return (
    <div className={`flex items-start gap-2 p-2.5 rounded-xl bg-surface-50 border border-surface-100 text-[11px] leading-snug text-surface-500 ${className}`}>
      <Icon className="w-4 h-4 text-surface-400 flex-shrink-0 mt-px" />
      <span>
        {onPhone
          ? 'Pick your UPI app to finish the payment. We save the expense when your UPI app opens.'
          : 'On a computer we show a QR. Scan it with any UPI app on your phone, then confirm here to save the expense.'}
      </span>
    </div>
  );
}
